import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useBookingRealtimeSync } from '@/hooks/useBookingRealtimeSync';

interface Appointment {
  id: string;
  start_time: string;
  end_time: string;
  status: string;
  format: string;
  price_at_booking: number;
  currency_at_booking: string;
  client_notes?: string;
  service_name?: string;
  practitioner_first_name?: string;
  practitioner_last_name?: string;
  client_first_name?: string;
  client_last_name?: string;
  payment_status: string;
  client_id: string;
  practitioner_id: string;
}

export const useAllAppointments = () => {
  const { user, activeRole } = useAuth();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const fetchAppointments = useMemo(() => {
    return async () => {
      if (!user || !activeRole) return;

      setIsLoading(true);
      setError(null);
      console.log('Fetching all appointments...');

      try {
        let query;

        if (activeRole === 'client') {
          const { data, error } = await supabase
            .from('bookings')
            .select(`
              id, 
              start_time, 
              end_time, 
              status, 
              format, 
              price_at_booking, 
              currency_at_booking, 
              client_notes, 
              location_details,
              session_instructions,
              refund_status,
              refund_reason,
              payment_status,
              service:service_id (service_name),
              practitioner:practitioner_id (first_name, last_name, avatar_url),
              practitioner_id,
              client_id
            `)
            .eq('client_id', user.id)
            .order('start_time', { ascending: false });

          if (error) {
            console.error('Error fetching client appointments:', error);
            setError(error.message);
            return;
          }

          if (!data) {
            setAppointments([]);
            return;
          }

          const transformedAppointments: Appointment[] = data.map((item: any) => ({
            id: item.id,
            start_time: item.start_time,
            end_time: item.end_time,
            status: item.status,
            format: item.format,
            price_at_booking: item.price_at_booking || 0,
            currency_at_booking: item.currency_at_booking || 'USD',
            client_notes: item.client_notes,
            service_name: item.service?.service_name,
            practitioner_first_name: item.practitioner?.first_name,
            practitioner_last_name: item.practitioner?.last_name,
            payment_status: item.payment_status,
            client_id: item.client_id,
            practitioner_id: item.practitioner_id,
          }));

          setAppointments(transformedAppointments);
        } else {
          const { data, error } = await supabase
            .from('bookings')
            .select(`
              id,
              start_time,
              end_time,
              status,
              format,
              payment_status,
              client_notes,
              practitioner_notes,
              refund_status,
              refund_reason,
              client_id,
              service_id,
              service:service_id (service_name),
              client:client_id (first_name, last_name),
              practitioner_id
            `)
            .eq('practitioner_id', user.id)
            .order('start_time', { ascending: false });

          if (error) {
            console.error('Error fetching practitioner appointments:', error);
            setError(error.message);
            return;
          }

          if (!data) {
            setAppointments([]);
            return;
          }

          const transformedAppointments: Appointment[] = data.map((item: any) => ({
            id: item.id,
            start_time: item.start_time,
            end_time: item.end_time,
            status: item.status,
            format: item.format,
            price_at_booking: 0,
            currency_at_booking: 'USD',
            client_notes: item.client_notes,
            service_name: item.service?.service_name,
            client_first_name: item.client?.first_name,
            client_last_name: item.client?.last_name,
            payment_status: item.payment_status,
            client_id: item.client_id,
            practitioner_id: item.practitioner_id,
          }));

          setAppointments(transformedAppointments);
        }

      } catch (err) {
        console.error('Unexpected error fetching appointments:', err);
        setError('Failed to fetch appointments');
      } finally {
        setIsLoading(false);
      }
    };
  }, [user, activeRole]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments, refreshTrigger]);

  const refreshAppointments = useMemo(() => {
    return () => {
      setRefreshTrigger(prev => prev + 1);
    };
  }, []);

  // Set up real-time synchronization
  useBookingRealtimeSync({
    userId: user?.id,
    type: activeRole as 'client' | 'practitioner',
    onBookingUpdate: refreshAppointments,
    onBookingInsert: refreshAppointments,
    onBookingDelete: refreshAppointments,
  });

  return {
    appointments,
    isLoading,
    error,
    fetchAppointments,
    refreshAppointments,
  };
};